import { useState, useContext, useEffect } from 'react';

import { Handle, Position } from '@xyflow/react';
import { useTranslate } from 'react-admin';

import NodeIcon from '../../components/NodeIcon';
import { personalEndpointContext } from '../../context/personalEndpointContext';
import { useIngordDataProvider } from '../../hooks/useIngordDataProvider';
import NodeHeader from '../NodeHeader';
import NodeWrapper from '../NodeWrapper';

import type { HuntType } from './HuntControls';
import type { NodeProps } from '@xyflow/react';
import type { GetOneResult } from 'react-admin';

/**
 * Renders a hunt node component.
 *
 * @return {React.ReactElement} The JSX element representing the hunt node component.
 */
export default function HuntNode({ data }: NodeProps & { data: HuntType }): React.ReactElement {
  const dataProvider = useIngordDataProvider();
  const [huntingGroup, setHuntingGroup] = useState<GetOneResult['data'] | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const translate = useTranslate();
  const isPersonal = useContext(personalEndpointContext);
  const huntResourceName = isPersonal ? `me/hunting_groups` : 'hunting_groups';

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (data.huntingGroupId !== undefined && data.huntingGroupId !== null && data.huntingGroupId !== '') {
        try {
          const response = await dataProvider.getOne(huntResourceName, { id: data.huntingGroupId });
          setHuntingGroup(response.data);
        } catch (_error) {
          setHuntingGroup(null);
        } finally {
          setLoaded(true);
        }
      }
    };
    void fetchData();
  }, [dataProvider, data.huntingGroupId, huntResourceName]);

  const isMissingHuntGroup =
    data.huntingGroupId === undefined || data.huntingGroupId === null || data.huntingGroupId === '';
  const errors =
    isMissingHuntGroup || (loaded && huntingGroup === null) ? ['callflows.nodes.hunt.huntingGroupErrorEmpty'] : [];

  return (
    <NodeWrapper errors={errors}>
      <NodeHeader
        title={huntingGroup?.name !== undefined ? huntingGroup.name : translate('callflows.nodes.hunt.name')}
        icon={<NodeIcon name='hunt' />}
      />
      <Handle type='target' position={Position.Left} id='hunt-target' />
      <Handle type='source' position={Position.Right} id='hunt-source' />
    </NodeWrapper>
  );
}
